import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { eurosToCents, formatCents } from '@/lib/format';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { Can } from '@/lib/permissions';

const LoyaltySettingsDoc = graphql(/* GraphQL */ `
  query LoyaltySettings {
    loyaltySettings {
      spendStepCents
      pointsPerStep
      updatedAt
    }
  }
`);

const SaveLoyaltySettingsDoc = graphql(/* GraphQL */ `
  mutation SaveLoyaltySettings($spendStepCents: Int!, $pointsPerStep: Int!) {
    saveLoyaltySettings(spendStepCents: $spendStepCents, pointsPerStep: $pointsPerStep) {
      spendStepCents
      pointsPerStep
      updatedAt
    }
  }
`);

const PREVIEW_BASE_CENTS = 500;

/** Same stepwise rule as Go `domain.PointsForCents`: whole blocks only. */
function pointsForCents(
  qualifyingCents: number,
  spendStepCents: number,
  pointsPerStep: number,
): number {
  if (qualifyingCents <= 0 || spendStepCents < 1 || pointsPerStep < 1) {
    return 0;
  }
  return Math.floor(qualifyingCents / spendStepCents) * pointsPerStep;
}

function pointWord(n: number): string {
  return n === 1 ? 'point' : 'points';
}

export function LoyaltyTab() {
  const { data, previousData, loading, error, refetch } = useQuery(LoyaltySettingsDoc, {
    errorPolicy: 'all',
  });
  const settings = (data ?? previousData)?.loyaltySettings;
  const [spendEuros, setSpendEuros] = useState('');
  const [pointsPerStep, setPointsPerStep] = useState('');
  const [seeded, setSeeded] = useState(false);
  if (settings && !seeded) {
    setSeeded(true);
    setSpendEuros(String(settings.spendStepCents / 100));
    setPointsPerStep(String(settings.pointsPerStep));
  }
  const [save, { loading: saving }] = useMutation(SaveLoyaltySettingsDoc);
  const [saveError, setSaveError] = useState<string | null>(null);

  const spendStepCents = eurosToCents(Number.parseFloat(spendEuros));
  const pointsN = Number.parseInt(pointsPerStep, 10);
  const pairValid =
    Number.isFinite(spendStepCents) && spendStepCents >= 1 && Number.isInteger(pointsN) && pointsN >= 1;
  const previewPoints = pairValid ? pointsForCents(PREVIEW_BASE_CENTS, spendStepCents, pointsN) : null;

  if (loading && !settings && !error) {
    return <p className="text-sm text-gray-500">Loading loyalty settings…</p>;
  }

  return (
    <FormLayout
      title="Loyalty"
      description="Per how many euros a guest earns how many points. Qualifying spend is PIN and online-ticket admission; tips, cash-trade, and the booking fee earn nothing. Crossing a threshold mints a voucher; redeeming it at the bar spends the snapshotted cost. Changing this re-prices every future award and requires a passkey session."
      onSubmit={async (event) => {
        event.preventDefault();
        setSaveError(null);
        if (!pairValid) {
          setSaveError('Enter a spend step of at least €0.01 and at least 1 point per step.');
          return;
        }
        try {
          await save({
            variables: { spendStepCents, pointsPerStep: pointsN },
          });
          await refetch();
        } catch (err) {
          setSaveError(graphqlErrorMessage(err));
        }
      }}
      footer={
        <Can
          permission="loyalty:adjust"
          fallback={
            <p className="text-sm text-gray-500">
              You can view this pair. Saving it requires loyalty:adjust (passkey).
            </p>
          }
        >
          <div className="flex items-center gap-3">
            <Button type="submit" id="admin.config.loyaltySave" loading={saving}>
              Save loyalty settings
            </Button>
            {saveError ? <span className="text-sm text-red-600">{saveError}</span> : null}
          </div>
        </Can>
      }
    >
      <FormSection
        title="Earning pair"
        description="Count whole steps only. Leftover qualifying spend below one step earns nothing."
      >
        {error ? <QueryErrorNotice error={error} title="Could not load loyalty settings" /> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Per (euros)"
            required
            htmlFor="admin.config.loyaltySpendStep"
            description="Qualifying spend that makes one step."
          >
            <Input
              id="admin.config.loyaltySpendStep"
              type="number"
              min={0.01}
              step="0.01"
              value={spendEuros}
              onChange={(e) => setSpendEuros(e.target.value)}
            />
          </FormField>
          <FormField
            label="Points awarded"
            required
            htmlFor="admin.config.loyaltyPointsPerStep"
            description="Points granted for each whole step."
          >
            <Input
              id="admin.config.loyaltyPointsPerStep"
              type="number"
              min={1}
              step={1}
              value={pointsPerStep}
              onChange={(e) => setPointsPerStep(e.target.value)}
            />
          </FormField>
        </div>
        {pairValid && previewPoints !== null ? (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Per {formatCents(spendStepCents)} → {pointsN} {pointWord(pointsN)}. A{' '}
            {formatCents(PREVIEW_BASE_CENTS)} bar spend earns {previewPoints}{' '}
            {pointWord(previewPoints)}.
          </p>
        ) : (
          <p className="text-sm text-gray-500">Enter a valid pair to preview the rule.</p>
        )}
      </FormSection>
    </FormLayout>
  );
}
